import gettext from '@sdk/lang/gettext';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import displayDuration from '@sdk/utils/displayDuration';
import getScheduledBreaks, {
  Props as CommonProps,
} from '@sdk/automatedBreaks/helpers/getScheduledBreaks';
import { AutomatedBreakPaidCategory } from '@sdk/automatedBreaks/types/AutomatedBreakPaidCategory';

type FormatDurationProps = {
  dtstart: Date;
  dtend: Date;
  durationInMinutes: number;
  usTimeFormat: boolean;
};
export const formatDuration = ({
  dtstart,
  dtend,
  durationInMinutes,
  usTimeFormat,
}: FormatDurationProps) =>
  `${formatTime(dtstart, usTimeFormat)} - ${formatTime(dtend, usTimeFormat)} · ${displayDuration(
    durationInMinutes
  )}`;

const calculateStandardBreaksDuration = (durationInMintues: number, breakDuration?: number) =>
  durationInMintues - (breakDuration || 0);

type CalculateAutomatedBreaksDurationProps = CommonProps & {
  durationInMinutes: number;
};

export const calculateAutomatedBreaksDuration = ({
  durationInMinutes,
  shift,
  automatedBreaksSettings,
}: CalculateAutomatedBreaksDurationProps) => {
  const { meal, rest } = getScheduledBreaks({ shift, automatedBreaksSettings });

  const mealDuration = meal * automatedBreaksSettings.meal.breakDuration;
  const restDuration = rest * automatedBreaksSettings.rest.breakDuration;

  let finalDurationInMinutes = durationInMinutes;

  if (automatedBreaksSettings.meal.isPaid === AutomatedBreakPaidCategory.unpaid) {
    finalDurationInMinutes -= mealDuration;
  }

  if (automatedBreaksSettings.rest.isPaid === AutomatedBreakPaidCategory.unpaid) {
    finalDurationInMinutes -= restDuration;
  }

  return finalDurationInMinutes;
};

type Props = CommonProps & {
  usTimeFormat: boolean;
  automatedBreaksFeatureFlag: boolean;
  isStandardBreakPaid: boolean;
};

const getDisplayLabel = ({
  shift,
  usTimeFormat,
  automatedBreaksFeatureFlag,
  automatedBreaksSettings,
  isStandardBreakPaid,
}: Props) => {
  const { dtstart, dtend, duration, breakDuration } = shift;
  const isOpenEnded = dtstart.toString() === dtend.toString();

  if (isOpenEnded) {
    return gettext('Starts at %(time)s', { time: formatTime(dtstart, usTimeFormat) });
  }

  let finalDurationMinutes = duration * 60;
  if (!automatedBreaksFeatureFlag || !automatedBreaksSettings.isEnabled) {
    finalDurationMinutes = isStandardBreakPaid
      ? finalDurationMinutes
      : calculateStandardBreaksDuration(finalDurationMinutes, breakDuration);
  } else {
    finalDurationMinutes = calculateAutomatedBreaksDuration({
      durationInMinutes: finalDurationMinutes,
      shift,
      automatedBreaksSettings,
    });
  }

  return formatDuration({ dtstart, dtend, durationInMinutes: finalDurationMinutes, usTimeFormat });
};

export default getDisplayLabel;
